import type { Timeframe } from '@shared/types/candle'
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts'
import type {
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  CandlestickSeriesOptions,
  LineSeriesOptions,
  AreaSeriesOptions,
  BaselineSeriesOptions,
  HistogramSeriesOptions,
  DeepPartial,
} from 'lightweight-charts'
import { useEffect, useRef } from 'react'

import { IST_OFFSET, type OhlcvBar } from '@/hooks/useCandles'
import { useChartTheme } from '@/hooks/useChartTheme'
import type { IndicatorResults } from '@/hooks/useCustomIndicatorValues'
import type { LiveTick } from '@/hooks/useLiveTick'
import { getIndicator } from '@/indicators/registry'
import type { ActiveIndicator, ChartType } from '@/types/chart'
import type { IndicatorResult } from '@/types/indicator'

/** Empty bar-widths of future space to the right of the last data bar. */
export const RIGHT_OFFSET = 20

interface ChartCanvasProps {
  bars: OhlcvBar[]
  chartType: ChartType
  timeframe: Timeframe
  indicators: ActiveIndicator[]
  indicatorValues: Map<string, IndicatorResult>
  isLoading: boolean
  isError: boolean
  liveTick?: LiveTick | null
  liveCandle?: OhlcvBar | null
  customIndicatorResults?: IndicatorResults
  onChartReady?: (chart: IChartApi) => void
  onChartDestroy?: (chart: IChartApi) => void
  onMainSeriesReady?: (series: AnySeriesApi) => void
}

export type AnySeriesApi =
  | ISeriesApi<'Candlestick'>
  | ISeriesApi<'Line'>
  | ISeriesApi<'Area'>
  | ISeriesApi<'Baseline'>

export function ChartCanvas({
  bars,
  chartType,
  timeframe: _timeframe,
  indicators,
  indicatorValues,
  isLoading,
  isError,
  liveTick,
  liveCandle,
  customIndicatorResults,
  onChartReady,
  onChartDestroy,
  onMainSeriesReady,
}: ChartCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<AnySeriesApi | null>(null)
  const volSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const lastBarRef = useRef<OhlcvBar | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlaySeriesRef = useRef(new Map<string, ISeriesApi<any>>())
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customSeriesRef = useRef(new Map<string, ISeriesApi<any>>())

  // Track first bar timestamp to detect symbol/TF change vs periodic refetch
  const prevFirstBarTimeRef = useRef<number | undefined>(undefined)

  const { chartOptions, candleColors } = useChartTheme()

  // ── Create chart on mount ────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      ...chartOptions,
      layout: {
        ...chartOptions.layout,
        background: {
          type: ColorType.Solid,
          color:
            (chartOptions.layout?.background as { color?: string } | undefined)?.color ?? '#131722',
        },
      },
      crosshair: {
        ...chartOptions.crosshair,
        mode: CrosshairMode.Normal,
      },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      // Mouse/trackpad drag pans; wheel pans (not zooms); pinch zooms.
      // On Mac the trackpad two-finger swipe fires as wheel events — keeping
      // handleScale.mouseWheel:true would make those events zoom instead of
      // scroll, which is the "drag zooms" behaviour users reported.
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: {
        mouseWheel: false, // disable wheel-zoom so trackpad swipe pans
        pinch: true, // explicit pinch gesture still zooms
        axisPressedMouseMove: false, // disable axis-drag rescaling
      },
      // Explicit right-margin so the user can pan freely into empty future space.
      // The theme also sets these, but we set them here directly so they are
      // guaranteed to be present regardless of theme spread order.
      timeScale: {
        ...chartOptions.timeScale,
        rightOffset: RIGHT_OFFSET,
        fixRightEdge: false,
        fixLeftEdge: false,
      },
    })

    chartRef.current = chart
    onChartReady?.(chart)

    // Volume histogram — pinned to the bottom 20% of the price pane
    const volSeries = chart.addHistogramSeries({
      priceScaleId: 'volume',
      priceLineVisible: false,
      lastValueVisible: false,
    } as DeepPartial<HistogramSeriesOptions>)
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    })
    volSeriesRef.current = volSeries

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        chart.applyOptions({ width, height })
      }
    })
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      onChartDestroy?.(chart)
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      volSeriesRef.current = null
      overlaySeriesRef.current = new Map()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Apply theme changes ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return
    chartRef.current.applyOptions({
      ...chartOptions,
      layout: {
        ...chartOptions.layout,
        background: {
          type: ColorType.Solid,
          color:
            (chartOptions.layout?.background as { color?: string } | undefined)?.color ?? '#131722',
        },
      },
    })
  }, [chartOptions])

  // ── Recreate main series when chartType changes ──────────────────────────────
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current)
      seriesRef.current = null
    }

    let series: AnySeriesApi

    if (chartType === 'candlestick') {
      series = chart.addCandlestickSeries({
        ...(candleColors as DeepPartial<CandlestickSeriesOptions>),
      })
    } else if (chartType === 'line') {
      series = chart.addLineSeries({
        color: '#2962ff',
        lineWidth: 2,
      } as DeepPartial<LineSeriesOptions>)
    } else if (chartType === 'area') {
      series = chart.addAreaSeries({
        topColor: 'rgba(41, 98, 255, 0.4)',
        bottomColor: 'rgba(41, 98, 255, 0.0)',
        lineColor: '#2962ff',
        lineWidth: 2,
      } as DeepPartial<AreaSeriesOptions>)
    } else {
      series = chart.addBaselineSeries({
        baseValue: { type: 'price', price: 0 },
        topLineColor: '#26a69a',
        topFillColor1: 'rgba(38, 166, 154, 0.28)',
        topFillColor2: 'rgba(38, 166, 154, 0.05)',
        bottomLineColor: '#ef5350',
        bottomFillColor1: 'rgba(239, 83, 80, 0.05)',
        bottomFillColor2: 'rgba(239, 83, 80, 0.28)',
      } as DeepPartial<BaselineSeriesOptions>)
    }

    seriesRef.current = series
    onMainSeriesReady?.(series)

    if (bars.length > 0) {
      setSeriesData(series, chartType, bars)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType])

  // ── Update candlestick colors on theme change ────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current || chartType !== 'candlestick') return
    const s = seriesRef.current as ISeriesApi<'Candlestick'>
    s.applyOptions(candleColors as DeepPartial<CandlestickSeriesOptions>)
  }, [candleColors, chartType])

  // ── Feed price data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current || bars.length === 0) return

    setSeriesData(seriesRef.current, chartType, bars)
    lastBarRef.current = bars[bars.length - 1] ?? null

    // Feed volume bars (green = up candle, red = down candle)
    if (volSeriesRef.current) {
      volSeriesRef.current.setData(
        bars.map((b) => ({
          time: b.time as UTCTimestamp,
          value: b.value ?? 0,
          color: b.close >= b.open ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)',
        }))
      )
    }

    // Only fitContent when symbol or timeframe changes (detected by a different
    // first-bar timestamp).  Periodic refetches keep the user's scroll position.
    const firstTime = bars[0]?.time
    if (firstTime !== prevFirstBarTimeRef.current) {
      const ts = chartRef.current?.timeScale()
      if (ts) {
        ts.fitContent()
        // fitContent resets rightOffset to 0 — restore our margin so the
        // user can always see and scroll into the empty future space.
        ts.applyOptions({ rightOffset: RIGHT_OFFSET })
      }
      prevFirstBarTimeRef.current = firstTime
    }
  }, [bars, chartType])

  // ── Render overlay indicator series ─────────────────────────────────────────
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const indicatorByInstance = new Map(indicators.map((ind) => [ind.instanceId, ind]))

    const wanted = new Set<string>()
    for (const [instanceId, result] of indicatorValues) {
      const ind = indicatorByInstance.get(instanceId)
      if (!ind) continue
      const entry = getIndicator(ind.id)
      if (!entry?.config.overlay) continue
      for (const line of result.lines) {
        wanted.add(`${instanceId}:${line.key}`)
      }
    }

    for (const [key, s] of overlaySeriesRef.current) {
      if (!wanted.has(key)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        chart.removeSeries(s)
        overlaySeriesRef.current.delete(key)
      }
    }

    if (!bars.length) return
    // Timestamps must align with the same barsWithLive array used by useIndicatorValues.
    const lastBar = bars[bars.length - 1]
    const barsForIndicators =
      liveCandle && lastBar
        ? liveCandle.time === lastBar.time
          ? [...bars.slice(0, -1), liveCandle]
          : liveCandle.time > lastBar.time
            ? [...bars, liveCandle]
            : bars
        : bars
    const timestamps = barsForIndicators.map((b) => b.time as UTCTimestamp)

    for (const [instanceId, result] of indicatorValues) {
      const ind = indicatorByInstance.get(instanceId)
      if (!ind) continue
      const entry = getIndicator(ind.id)
      if (!entry?.config.overlay) continue

      for (const line of result.lines) {
        const key = `${instanceId}:${line.key}`
        const def = entry.config.lines.find((l) => l.key === line.key)
        if (!def) continue

        const color = ind.colors?.[line.key] ?? def.color

        const isDots = def.renderStyle === 'dots'
        const isArea = def.renderStyle === 'area'
        const isHistogram = def.renderStyle === 'histogram'
        const lineStyleVal =
          def.style === 'dashed'
            ? LineStyle.Dashed
            : def.style === 'dotted'
              ? LineStyle.Dotted
              : LineStyle.Solid
        let s = overlaySeriesRef.current.get(key)
        if (!s) {
          if (isHistogram) {
            s = chart.addHistogramSeries({
              color,
              priceScaleId: 'volume',
              priceLineVisible: false,
              lastValueVisible: false,
            } as DeepPartial<HistogramSeriesOptions>)
            // Volume occupies the bottom 20% of the price pane, matching TradingView
            chart.priceScale('volume').applyOptions({
              scaleMargins: { top: 0.8, bottom: 0 },
            })
          } else if (isArea) {
            const fillColor = def.areaFillColor ?? color
            s = chart.addAreaSeries({
              lineColor: color,
              lineWidth: def.width as 1 | 2 | 3 | 4,
              lineStyle: lineStyleVal,
              topColor: fillColor,
              bottomColor: 'rgba(0,0,0,0)', // fade to transparent so candles below are never covered
              priceLineVisible: false,
              lastValueVisible: true,
            } as DeepPartial<AreaSeriesOptions>)
          } else {
            s = chart.addLineSeries({
              color,
              lineWidth: def.width as 1 | 2 | 3 | 4,
              lineStyle: lineStyleVal,
              priceLineVisible: false,
              lastValueVisible: !isDots,
              // Parabolic SAR and similar dot-only indicators: show markers, hide line
              ...(isDots
                ? { lineVisible: false, pointMarkersVisible: true, pointMarkersRadius: 2 }
                : {}),
            } as DeepPartial<LineSeriesOptions>)
          }
          overlaySeriesRef.current.set(key, s)
        } else {
          if (isHistogram) {
            // Per-bar colors are set in setData; no single color to update
          } else if (isArea) {
            const fillColor = def.areaFillColor ?? color
            s.applyOptions({
              lineColor: color,
              topColor: fillColor,
              bottomColor: 'rgba(0,0,0,0)',
            } as DeepPartial<AreaSeriesOptions>)
          } else {
            s.applyOptions({ color } as DeepPartial<LineSeriesOptions>)
          }
        }

        if (isHistogram) {
          const histData = timestamps
            .map((t, i) => {
              const v = line.values[i]
              if (v === null || v === undefined) return null
              return { time: t, value: v, color: line.colors?.[i] ?? color }
            })
            .filter(
              (d): d is { time: UTCTimestamp; value: number; color: string } =>
                d !== null && d.color !== null
            )
          s.setData(histData)
        } else {
          const data = timestamps
            .map((t, i) => {
              const v = line.values[i]
              return v !== null && v !== undefined ? { time: t, value: v } : null
            })
            .filter((d): d is { time: UTCTimestamp; value: number } => d !== null)
          s.setData(data)
        }
      }
    }
  }, [indicatorValues, bars, liveCandle, indicators])

  // ── Custom indicator overlay series ─────────────────────────────────────────
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !bars.length) return

    const timestamps = bars.map((b) => b.time as UTCTimestamp)
    const results: IndicatorResults = customIndicatorResults ?? new Map<string, never[]>()

    // Remove series that are no longer present
    const wanted = new Set<string>()
    for (const [indId, plots] of results) {
      for (const plot of plots) wanted.add(`${indId}:${plot.key}`)
    }
    for (const [key, s] of customSeriesRef.current) {
      if (!wanted.has(key)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        chart.removeSeries(s)
        customSeriesRef.current.delete(key)
      }
    }

    for (const [indId, plots] of results) {
      for (const plot of plots) {
        const key = `${indId}:${plot.key}`
        let s = customSeriesRef.current.get(key)
        if (!s) {
          if (plot.type === 'histogram') {
            s = chart.addHistogramSeries({
              color: plot.color,
              priceLineVisible: false,
              lastValueVisible: false,
            } as DeepPartial<HistogramSeriesOptions>)
          } else {
            s = chart.addLineSeries({
              color: plot.color,
              lineWidth: (plot.lineWidth as 1 | 2 | 3 | 4) ?? 1,
              priceLineVisible: false,
              lastValueVisible: true,
            } as DeepPartial<LineSeriesOptions>)
          }
          customSeriesRef.current.set(key, s)
        } else {
          s.applyOptions({ color: plot.color })
        }

        const data = timestamps
          .map((t, i) => {
            const v = plot.values[i]
            return v !== null && v !== undefined ? { time: t, value: v } : null
          })
          .filter((d): d is { time: UTCTimestamp; value: number } => d !== null)
        s.setData(data)
      }
    }
  }, [customIndicatorResults, bars])

  // ── Live OHLCV candle update ─────────────────────────────────────────────────
  useEffect(() => {
    const series = seriesRef.current
    if (!series || !liveCandle) return
    updateSeriesLastBar(series, chartType, liveCandle)
    lastBarRef.current = liveCandle
    if (volSeriesRef.current && liveCandle.value != null) {
      volSeriesRef.current.update({
        time: liveCandle.time as UTCTimestamp,
        value: liveCandle.value,
        color: liveCandle.close >= liveCandle.open ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)',
      })
    }
  }, [liveCandle, chartType])

  // ── Live price tick update ───────────────────────────────────────────────────
  useEffect(() => {
    const series = seriesRef.current
    if (!series || !liveTick) return

    const tickTs = Math.floor(liveTick.timestamp / 1000) + IST_OFFSET
    const last = lastBarRef.current
    const price = liveTick.price

    const liveBar: OhlcvBar = last
      ? {
          time: last.time,
          open: last.open,
          high: Math.max(last.high, price),
          low: Math.min(last.low, price),
          close: price,
          value: price,
        }
      : { time: tickTs, open: price, high: price, low: price, close: price, value: price }

    updateSeriesLastBar(series, chartType, liveBar)
  }, [liveTick, chartType])

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-tv-text-muted">Loading…</span>
        </div>
      )}
      {isError && !isLoading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-tv-red">Failed to load candles</span>
        </div>
      )}
      {!isLoading && !isError && bars.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-tv-text-muted">No data available</span>
        </div>
      )}
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function updateSeriesLastBar(series: AnySeriesApi, chartType: ChartType, bar: OhlcvBar) {
  if (chartType === 'candlestick') {
    const s = series as ISeriesApi<'Candlestick'>
    s.update({
      time: bar.time as UTCTimestamp,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    })
  } else {
    const s = series as ISeriesApi<'Line'> | ISeriesApi<'Area'> | ISeriesApi<'Baseline'>
    s.update({ time: bar.time as UTCTimestamp, value: bar.close })
  }
}

function setSeriesData(series: AnySeriesApi, chartType: ChartType, bars: OhlcvBar[]) {
  const toValue = (b: OhlcvBar) => ({ time: b.time as UTCTimestamp, value: b.close })
  if (chartType === 'candlestick') {
    const s = series as ISeriesApi<'Candlestick'>
    s.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
      }))
    )
  } else if (chartType === 'line') {
    const s = series as ISeriesApi<'Line'>
    s.setData(bars.map(toValue))
  } else if (chartType === 'area') {
    const s = series as ISeriesApi<'Area'>
    s.setData(bars.map(toValue))
  } else {
    const s = series as ISeriesApi<'Baseline'>
    s.setData(bars.map(toValue))
  }
}
